/**
 * Where the brand tokens actually reach the page.
 *
 * TWO "WHICH THEME AM I" SWITCHES WOULD BE ONE TOO MANY. Mantine sets
 * `data-mantine-color-scheme="dark"` on the page root and scopes its own variables to it. The
 * brand stylesheet scopes its variables to `data-theme='light'`. Ship both and they can disagree
 * — buttons dark, page light. So Mantine owns the switch and the brand tokens are emitted THROUGH
 * Mantine: this resolver's `light` and `dark` buckets are scoped to Mantine's own attribute for
 * us. One attribute, one source of truth, no synchronisation code. The brand's `data-theme`
 * attribute does not exist in our apps (docs/ui/02-theme.md and :396-399).
 *
 * All three keys — `variables`, `light`, `dark` — are required by the signature.
 *
 * ONE DETAIL THAT WOULD OTHERWISE WASTE AN AFTERNOON: Mantine strips any variable whose value
 * equals its own default, and it does that only while `cssVariablesSelector` is left at its
 * default. Our `--mx-*` names are not Mantine's, so nothing of ours is ever stripped — but do not
 * change `cssVariablesSelector` casually (docs/ui/02-theme.md).
 */
import type { CSSVariablesResolver } from '@mantine/core';
import {
  appFrame,
  appReading,
  brand,
  elevation,
  motion,
  radiusExtra,
  space,
  spacePage,
  typeExtra,
} from './tokens';
import type { BrandPalette } from './tokens';

/** The app's article screen, for the cabinet's article preview (./tokens, `appReading`). */
const appPalette = (
  t: (typeof appReading)['dark'] | (typeof appReading)['light'],
): Record<`--${string}`, string> => ({
  '--mx-app-bg': t.bg,
  '--mx-app-cell': t.cell,
  '--mx-app-stub': t.stub,
  '--mx-app-divider': t.divider,
  '--mx-app-text': t.text,
  '--mx-app-secondary': t.secondary,
  '--mx-app-tag': t.tag,
  '--mx-app-quote-bar': t.quoteBar,
  '--mx-app-link': t.link,
});

/** The palette, for whichever theme bucket it is handed. */
const palette = (t: BrandPalette): Record<`--${string}`, string> => ({
  '--mx-bg': t.bg,
  '--mx-bg2': t.bg2,
  '--mx-surface': t.surface,
  '--mx-surface2': t.surface2,
  '--mx-surface4': t.surface4,
  '--mx-border': t.border,
  '--mx-border2': t.border2,
  '--mx-border-dim': t.borderDim,
  '--mx-border-dash': t.borderDash,
  '--mx-accent': t.accent,
  '--mx-accent-hover': t.accentHover,
  '--mx-text': t.text,
  '--mx-on-accent': t.onAccent,
  '--mx-m1': t.m1,
  '--mx-m2': t.m2,
  '--mx-m3': t.m3,
  '--mx-m4': t.m4,
  '--mx-m5': t.m5,
  '--mx-m6': t.m6,
  '--mx-m7': t.m7,
  '--mx-m8': t.m8,
  '--mx-m9': t.m9,
  '--mx-danger': t.danger,
  '--mx-on-danger': t.onDanger,
  '--mx-success': t.success,
  '--mx-warning': t.warning,
  '--mx-info': t.info,
  '--mx-chart1': t.chart1,
  '--mx-chart2': t.chart2,
  '--mx-chart3': t.chart3,
  '--mx-chart4': t.chart4,
  '--mx-chart5': t.chart5,
  '--mx-chart6': t.chart6,
  '--mx-chart7': t.chart7,
  '--mx-chart8': t.chart8,
  '--mx-chart9': t.chart9,
  '--mx-chart10': t.chart10,
  '--mx-chart11': t.chart11,
});

/**
 * `--surface3` is deliberately absent. The prototype writes `var(--surface3, var(--surface4))`
 * eleven times and defines `--surface3` in neither theme, so it silently resolves to `surface4`
 * every time — the only undefined colour reference among the drawing's 33 distinct ones. Shipping
 * the name would ship a dangling fallback (docs/ui/01-tokens.md).
 *
 * `--mx-warning-ink` IS AN ALIAS, NOT A COLOUR: the ink for warning TEXT, one existing token per
 * scheme. `warning` reads as text in dark (8.54 on its own 0.14 tint over surface2, 6.49 over the
 * hovered row), but in light it does not - 3.78 on that tint over the page, 4.09 over white - and
 * the palette is ruled "match the website exactly", so no new value may be minted for it. Light
 * points at `accent-hover` (5.73 and 6.21 on the same tints) and dark at `warning`. A bare
 * triplet like every palette entry, so `oklch(var(--mx-warning-ink))` and `oklch(var(--mx-warning-
 * ink) / 0.5)` both work; emitted here, beside the palette, so it exists wherever the palette does.
 *
 * `--mantine-color-anchor` IN LIGHT IS THE ONE MANTINE VARIABLE THIS RESOLVER SETS. Mantine
 * computes the link from the theme, `var(--mantine-color-moon-7)` - which is `accent`: 4.10 on
 * surface2, 4.49 in a blue light Alert, 4.21 in a moon one - and emits it in the light block of
 * its runtime `<style>`, which the document places AFTER ./global.css, so an override there loses
 * (./global.css says why, at its light block). Set here, the value replaces Mantine's in that very
 * block. `accent-hover`: 5.53, 6.06 and 5.68, and 4.74 at worst, on surface4. Dark keeps Mantine's
 * own, `moon-4`, which reads there.
 */
export const cssVariablesResolver: CSSVariablesResolver = () => ({
  variables: {
    // Spacing — the full twelve-step scale, plus the page gutter, which is not a step.
    '--mx-space-0': space[0],
    '--mx-space-1': space[1],
    '--mx-space-2': space[2],
    '--mx-space-3': space[3],
    '--mx-space-4': space[4],
    '--mx-space-5': space[5],
    '--mx-space-6': space[6],
    '--mx-space-7': space[7],
    '--mx-space-8': space[8],
    '--mx-space-9': space[9],
    '--mx-space-10': space[10],
    '--mx-space-11': space[11],
    '--mx-space-page': spacePage,

    // Type roles with no Mantine slot.
    '--mx-fs-display': typeExtra.fsDisplay,
    '--mx-fs-prose': typeExtra.fsProse,
    '--mx-lh-prose': typeExtra.lhProse,
    '--mx-fs-nano': typeExtra.fsNano,
    '--mx-ls-nano': typeExtra.lsNano,
    '--mx-ls-eyebrow': typeExtra.lsEyebrow,
    '--mx-ls-tight': typeExtra.lsTight,

    // Motion — theme-agnostic. `--mx-dur` is 250ms; see the note in ./tokens.
    '--mx-dur-fast': motion.durFast,
    '--mx-dur': motion.dur,
    '--mx-dur-slow': motion.durSlow,
    '--mx-ease-out': motion.easeOut,
    '--mx-ease-moon': motion.easeMoon,

    // Radius steps outside Mantine's five.
    '--mx-radius-pill': radiusExtra.pill,
    '--mx-radius-full': radiusExtra.full,

    // The article preview's phone, the same in both schemes.
    '--mx-app-bezel': appFrame.bezel,
    '--mx-app-star': appFrame.star,
  },
  light: {
    ...palette(brand.light),
    ...appPalette(appReading.light),
    '--mx-warning-ink': brand.light.accentHover,
    '--mx-elev-1': elevation.light[1],
    '--mx-elev-2': elevation.light[2],
    '--mx-elev-3': elevation.light[3],
    '--mantine-color-anchor': 'oklch(var(--mx-accent-hover))',
  },
  dark: {
    ...palette(brand.dark),
    ...appPalette(appReading.dark),
    '--mx-warning-ink': brand.dark.warning,
    '--mx-elev-1': elevation.dark[1],
    '--mx-elev-2': elevation.dark[2],
    '--mx-elev-3': elevation.dark[3],
  },
});
